import { ClassNameMap, Styles, WithStylesOptions } from '@material-ui/styles';
import { Color, Theme, ThemeOptions, makeStyles } from '@material-ui/core';
import {
  ColorPartial,
  Palette,
  PaletteOptions,
} from '@material-ui/core/styles/createPalette';
import { Shape, ShapeOptions } from '@material-ui/core/styles/shape';
import { createTheme, useTheme } from '@material-ui/core';

import colors from './colors';
import tinycolor from 'tinycolor2';

interface P44NamedColor {
  status: {
    delivered: string;
    discharged: string;
    intransit: string;
    pending: string;
    unknown: string;
  };
}

interface P44NamedColorPartial {
  status?: Partial<P44NamedColor['status']>;
}

interface P44Palette extends Palette {
  white: string;
  named: P44NamedColor;
  blue: Color;
  green: Color;
  orange: Color;
  pink: Color;
  red: Color;
  teal: Color;
  yellow: Color;
}

/**
 * this should have the same keys as P44Palette with partial types
 */
interface P44PaletteOptions extends PaletteOptions {
  white?: string;
  named?: P44NamedColorPartial;
  blue?: ColorPartial;
  green?: ColorPartial;
  orange?: ColorPartial;
  pink?: ColorPartial;
  red?: ColorPartial;
  teal?: ColorPartial;
  yellow?: ColorPartial;
}

interface P44Shape extends Shape {
  borderRadiusSmall: number;
}

/**
 * this should have the same keys as P44Shape with partial types
 */
interface P44ShapeOptions extends ShapeOptions {
  borderRadiusSmall?: number;
}

export interface P44Theme extends Theme {
  palette: P44Palette;
  shape: P44Shape;
}

/**
 * this should have the same keys as P44Theme with partial types
 */
export interface P44ThemeOptions extends ThemeOptions {
  palette?: P44PaletteOptions;
  shape?: P44ShapeOptions;
}

export function useP44Theme() {
  return useTheme<P44Theme>();
}

export function makeP44Styles<
  Props extends Record<string, unknown> = Record<string, unknown>,
  ClassKey extends string = string,
>(
  styles: Styles<P44Theme, Props, ClassKey>,
  options?: Omit<WithStylesOptions<P44Theme>, 'withTheme'>,
): (props?: any) => ClassNameMap<ClassKey> {
  return makeStyles<P44Theme, Props, ClassKey>(styles, options);
}

export function createP44Theme(): P44Theme {
  /**
   * Seperating palette, shape, and typography generation to eagerly merge config with defaults.
   * This provides easier access to automatically calculated semantic rules such as palette.action.disabled
   */
  const themeOptionsWithoutOverrides: P44ThemeOptions = {
    palette: {
      ...colors,
      named: {
        status: {
          delivered: '#64b432',
          discharged: '#64b432',
          intransit: colors.teal[600],
          pending: '#ffcf3d',
          unknown: '#616a70',
        },
      },
      primary: {
        main: '#07456B',
      },
      secondary: {
        main: colors.blue[400],
      },
      error: {
        light: '#DB4243',
        main: '#DB4243',
        dark: '#930D0E',
      },
      success: {
        main: '#005A00',
        dark: '#005A00',
      },
      background: {
        default: colors.white,
      },
    },
    shape: {
      borderRadiusSmall: 2,
      borderRadius: 4,
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      fontSize: 14,
      fontWeightBold: 700,
      fontWeightMedium: 600,
      fontWeightRegular: 400,
      subtitle2: {
        color: 'rgba(0, 0, 0, 0.87)',
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: 1.57,
        letterSpacing: '0.00714em',
      },
      body2: {
        color: 'rgba(0, 0, 0, 0.87)',
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        letterSpacing: '0.01071em',
      },
      caption: {
        color: 'rgba(0, 0, 0, 0.87)',
        fontWeight: 400,
        fontSize: '0.75rem',
        lineHeight: 1.66,
        letterSpacing: '0.03333em',
      },
    },
    breakpoints: {
      keys: ['xs', 'sm', 'md', 'lg', 'xl'],
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  };

  const { palette, typography } = createTheme(themeOptionsWithoutOverrides);

  const overrides: P44ThemeOptions['overrides'] = {
    MuiChip: {
      root: {
        alignItems: 'start',
        backgroundColor: palette.common.white,
        color: palette.grey[600],
        height: 'auto',
        lineHeight: 1.5,
        fontSize: 14,
        '& .MuiChip-avatar': {
          background: 'transparent',
          margin: `3px -6px 0 4px`,
        },
        '& .MuiCircularProgress-root': {
          color: palette.grey[300],
          height: `15px !important`,
          width: `15px !important`,
        },
        '& .MuiSvgIcon-root': {
          color: palette.grey[300],
        },
      },
      outlined: {
        border: `1px solid ${palette.grey[300]}`,
        backgroundColor: palette.common.white,
      },
      clickable: {
        '&:hover': {
          backgroundColor: palette.grey[200],
        },
        '&:focus': {
          backgroundColor: palette.common.white,
        },
      },
      deleteIcon: {
        width: 30,
        margin: `5px 5px 0 -2px`,
      },
      label: {
        height: 'auto',
        overflow: 'auto',
        padding: '6px 12px',
        whiteSpace: 'normal',
        textOverflow: 'initial',
        width: '100%',
      },
    },
    MuiInputLabel: {
      filled: {
        '&$shrink': {
          // @transform
          // This keeps Chips within Autocomplete from getting too close to the label
          transform: 'translate(12px, 8px) scale(0.75)',
        },
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: palette.grey[50],
        '&:hover': {
          backgroundColor: palette.grey[200],
        },
        '&$focused': {
          backgroundColor: palette.grey[50],
        },
      },
      underline: {
        '&:after': {
          borderBottom: `2px solid ${palette.grey[500]}`,
        },
      },
    },
    MuiButton: {
      root: {
        minWidth: 40,
        '&$disabled': {
          borderColor: 'rgba(0, 0, 0, 0.12)',
          color: 'rgba(0, 0, 0, 0.26)',
        },
      },
      contained: {
        backgroundColor: palette.grey[600],
        color: palette.common.white,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: tinycolor(palette.grey[600]).darken(12).toString(),
        },
        '&$disabled': {
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
        },
      },
      text: {
        color: palette.grey[600],
        '&:hover': {
          backgroundColor: tinycolor(palette.grey[600])
            .setAlpha(0.08)
            .toRgbString(),
        },
      },
      // we have to manually specify color and border color due to overriding default styles. This can be avoided by specifying grey defaults within palette: https://material-ui.com/customization/default-theme/#default-theme
      outlined: {
        color: palette.grey[600],
        borderColor: palette.grey[600],
        '&:hover': {
          color: palette.grey[600],
          borderColor: palette.grey[600],
          backgroundColor: tinycolor(palette.grey[600])
            .setAlpha(0.08)
            .toRgbString(),
        },
        '&$disabled': {
          borderColor: 'rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiIconButton: {
      sizeSmall: {
        padding: 6,
      },
    },
    MuiLink: {
      root: {
        color: palette.secondary.main,
      },
    },
    MuiMenuItem: {
      root: {
        '&:hover': {
          backgroundColor: palette.grey[50],
        },
        '&:selected': {
          backgroundColor: palette.grey[50],
        },
        '&:focus': {
          backgroundColor: palette.grey[50],
        },
      },
    },
    MuiTableCell: {
      root: {
        fontSize: '0.8125rem',
        letterSpacing: 0,
      },
    },
    MuiTooltip: {
      tooltip: {
        '& a': {
          color: palette.common.white,
          textDecoration: 'none',
          fontWeight: typography.fontWeightBold,
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: palette.grey[900],
      },
    },
  };

  return createTheme({
    ...themeOptionsWithoutOverrides,
    overrides,
  }) as P44Theme;
}
